import {
    BoxedGroup,
    Button,
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@schuettflix/planum-react';
import { DeleteIcon } from '@schuettflix/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';

type DeleteAllProductsButtonProps = {
    onDeleteAsync: () => Promise<void>;
};

export function DeleteAllProductsButton({ onDeleteAsync }: DeleteAllProductsButtonProps) {
    const { t } = useTranslation();
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [isDeleteLoading, setIsDeleteLoading] = useState(false);

    const handleDelete = async () => {
        if (isDeleteLoading) {
            return;
        }

        setIsDeleteLoading(true);

        try {
            await onDeleteAsync();
            setDialogOpen(false);
        } catch (error) {
            Sentry.captureException(error);
        } finally {
            setIsDeleteLoading(false);
        }
    };

    return (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
                <Button variant="tertiary" name="delete-all-products-button" data-test="delete-all-products-button">
                    <DeleteIcon size="xs" monochrome />
                    {t('sections.productGroupSelection.deleteAllProducts')}
                </Button>
            </DialogTrigger>
            <DialogContent size="sm">
                <DialogHeader>
                    <DialogTitle>{t('sections.productGroupSelection.askProductDeletion')}</DialogTitle>
                </DialogHeader>
                <DialogBody>{t('sections.productGroupSelection.productDeletionHint')}</DialogBody>
                <DialogFooter>
                    <BoxedGroup>
                        <Button
                            variant="primary"
                            name="confirm-delete-products-button"
                            loading={isDeleteLoading}
                            onClick={handleDelete}
                        >
                            {t('sections.productGroupSelection.confirmProductDeletion')}
                        </Button>
                        <DialogClose asChild>
                            <Button variant="secondary" disabled={isDeleteLoading}>
                                {t('sections.productGroupSelection.cancelProductDeletion')}
                            </Button>
                        </DialogClose>
                    </BoxedGroup>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
